/**
 * TokenPopover数据处理Hook
 * 统一处理Token、Stock和Pool三种资产类型的数据获取、格式化和状态管理
 */

import { useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTokenDetailsMarket } from "../useTokenDetailsMarket";
import { useStockDetailsMarket } from "../useStockDetailsMarket";
import type {
  CardItem,
  UseTokenPopoverDataReturn,
  TokenApiResponse,
  TokenQueryParams,
} from "./types";
import {
  SUPPORTED_ASSET_TYPES,
  TOKEN_QUERY_PARAMS,
  STOCK_QUERY_PARAMS,
  ERROR_MESSAGES,
} from "./constants";
import {
  generateTimeRange,
  transformTokenApiData,
  transformStockApiData,
  validateTokenData,
  validateStockData,
  handleError,
  isAssetType,
} from "./utils";
import { Stock } from "@features/api/openData";

const timeRange = generateTimeRange();

/**
 * TokenPopover数据处理Hook
 * @param cardItem 卡片项目数据
 * @returns Hook返回值
 */
export const useTokenPopoverData = (
  cardItem: CardItem | null,
): UseTokenPopoverDataReturn => {
  const queryClient = useQueryClient();

  // 判断是否应该显示TokenPopover
  const shouldShow = useMemo(() => {
    if (!cardItem || !cardItem.type) {
      return false;
    }

    const isSupported = SUPPORTED_ASSET_TYPES.includes(cardItem.type);

    return isSupported;
  }, [cardItem]);

  // Token/Pool数据查询参数
  const tokenQueryParams = useMemo((): TokenQueryParams => {
    return {
      ...TOKEN_QUERY_PARAMS,
      "enrich.with_ohlc.start_time": timeRange.startTime,
      "enrich.with_ohlc.end_time": timeRange.endTime,
    };
  }, []);

  // 判断是否为Token或Pool类型
  const isTokenOrPool = useMemo(() => {
    return (
      shouldShow &&
      cardItem &&
      (cardItem.type === "token" || cardItem.type === "pool")
    );
  }, [shouldShow, cardItem]);

  // 判断是否为Stock类型
  const isStock = useMemo(() => {
    return shouldShow && cardItem && cardItem.type === "stock";
  }, [shouldShow, cardItem]);
  // Token/Pool数据获取
  const {
    data: tokenRawData,
    isLoading: tokenLoading,
    isSuccess: tokenSuccess,
    queryKey,
  } = useTokenDetailsMarket({
    enabled: !!(isTokenOrPool && !!cardItem?.properties?.id),
    ids: cardItem?.properties?.id ? [cardItem.properties.id] : [],
    isPollToken: cardItem?.type === "pool",
    queryParams: tokenQueryParams,
    useQueryParams: {
      staleTime: 5 * 60 * 1000, // 5分钟内不重新请求
      gcTime: 10 * 60 * 1000, // 10分钟后进行垃圾回收
    },
  });

  // Stock数据获取
  const {
    data: stockRawData,
    isLoading: stockLoading,
    isSuccess: stockSuccess,
    queryKey: stockQueryKey,
  } = useStockDetailsMarket({
    enabled: !!(isStock && !!cardItem?.properties?.symbol),
    symbol: cardItem?.properties?.symbol,
    queryParams: STOCK_QUERY_PARAMS,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新请求
    gcTime: 10 * 60 * 1000, // 10分钟后进行垃圾回收
  });

  // 处理Token/Pool数据转换
  const processTokenData = useCallback(
    (rawData: any[]) => {
      try {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
          return null;
        }

        const firstItem = rawData[0];

        if (!validateTokenData(firstItem)) {
          handleError(
            new Error(ERROR_MESSAGES.DATA_FORMAT_ERROR),
            cardItem?.type || "unknown",
            "validation",
          );
          return null;
        }

        const assetType = cardItem?.type;
        const tokenId = cardItem?.properties?.id;
        const tokenType = cardItem?.type;
        if (assetType && isAssetType(assetType)) {
          return transformTokenApiData(
            firstItem as TokenApiResponse,
            assetType,
            tokenId,
            tokenType,
          );
        }
        return transformTokenApiData(
          firstItem as TokenApiResponse,
          "token",
          tokenId,
          tokenType,
        );
      } catch (error) {
        handleError(
          error,
          cardItem?.type || "unknown",
          "token data processing",
        );
        return null;
      }
    },
    [cardItem?.type],
  );

  // 处理Stock数据转换
  const processStockData = useCallback(
    (rawData: Stock[]) => {
      try {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
          return null;
        }

        const firstItem = rawData[0];

        if (!validateStockData(firstItem)) {
          handleError(
            new Error(ERROR_MESSAGES.DATA_FORMAT_ERROR),
            cardItem?.type || "unknown",
            "validation",
          );
          return null;
        }

        const tokenType = cardItem?.type;
        return transformStockApiData(firstItem, tokenType);
      } catch (error) {
        handleError(
          error,
          cardItem?.type || "unknown",
          "stock data processing",
        );
        return null;
      }
    },
    [cardItem?.type],
  );

  // 统一的数据处理
  const processedData = useMemo(() => {
    if (!shouldShow) {
      return null;
    }

    // 处理Token/Pool数据
    if (isTokenOrPool && tokenSuccess && tokenRawData) {
      return processTokenData(tokenRawData);
    }
    // 处理Stock数据
    if (isStock && stockSuccess && stockRawData) {
      return processStockData([stockRawData]);
    }

    return null;
  }, [
    shouldShow,
    isTokenOrPool,
    isStock,
    tokenSuccess,
    stockSuccess,
    tokenRawData,
    stockRawData,
    processTokenData,
    processStockData,
  ]);

  // 统一的加载状态
  const isLoading = useMemo(() => {
    if (!shouldShow) {
      return false;
    }

    if (isTokenOrPool) {
      return tokenLoading;
    }

    if (isStock) {
      return stockLoading;
    }

    return false;
  }, [shouldShow, isTokenOrPool, isStock, tokenLoading, stockLoading]);

  // 错误处理
  const error = useMemo(() => {
    if (!shouldShow) {
      return null;
    }

    // 检查必要参数
    if (isTokenOrPool && !cardItem?.properties?.id) {
      return new Error(ERROR_MESSAGES.MISSING_REQUIRED_FIELD + ": id");
    }

    if (isStock && !cardItem?.properties?.symbol) {
      return new Error(ERROR_MESSAGES.MISSING_REQUIRED_FIELD + ": symbol");
    }

    // 检查数据获取是否成功但数据为空
    if ((tokenSuccess && !tokenRawData) || (stockSuccess && !stockRawData)) {
      return new Error(ERROR_MESSAGES.API_FETCH_ERROR);
    }

    return null;
  }, [
    shouldShow,
    isTokenOrPool,
    isStock,
    cardItem?.properties,
    tokenSuccess,
    stockSuccess,
    tokenRawData,
    stockRawData,
  ]);

  // 缓存失效方法
  const invalidateCache = useCallback(async () => {
    const promises: Promise<void>[] = [];

    // Token/Pool数据缓存失效
    if (queryKey && isTokenOrPool) {
      promises.push(queryClient.invalidateQueries({ queryKey: [queryKey] }));
    }

    // Stock数据缓存失效
    if (stockQueryKey && isStock) {
      promises.push(
        queryClient.invalidateQueries({ queryKey: [...stockQueryKey] }),
      );
    }

    // 等待所有缓存失效操作完成
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }, [queryClient, queryKey, stockQueryKey, isTokenOrPool, isStock]);

  // 返回最终结果
  return {
    data: processedData,
    isLoading,
    error,
    shouldShow,
    invalidateCache,
  };
};

// 导出类型
export type { CardItem, UseTokenPopoverDataReturn } from "./types";
